"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useState } from "react";
import { CheckCircle, ArrowRight, Send, Loader2, MessageSquare, LayoutDashboard, Clock, Smartphone } from "lucide-react";
import { useTranslations, useLocale } from "next-intl";
import { Link } from "@/i18n/navigation";
import { api } from "@/lib/api";

interface FormValues {
  name: string;
  facilityType: string;
  email: string;
  phone: string;
  service: string;
  details: string;
}

const EMPTY_FORM: FormValues = {
  name: "",
  facilityType: "",
  email: "",
  phone: "",
  service: "",
  details: "",
};

export function RequestFormSection() {
    const t = useTranslations("RequestForm");
    const locale = useLocale();
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [values, setValues] = useState<FormValues>(EMPTY_FORM);
    const [errors, setErrors] = useState<Partial<Record<keyof FormValues, string>>>({});

    const setField = (key: keyof FormValues) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setValues((v) => ({ ...v, [key]: e.target.value }));
        setErrors((er) => ({ ...er, [key]: undefined }));
    };

    const validate = (data: FormValues): Partial<Record<keyof FormValues, string>> => {
        const errs: Partial<Record<keyof FormValues, string>> = {};
        if (data.name.trim().length < 2) errs.name = t("validation.name_min");
        if (data.facilityType.trim().length < 2) errs.facilityType = t("validation.facility_required");
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) errs.email = t("validation.email_invalid");
        if (data.phone.replace(/\D/g, '').length < 10) errs.phone = t("validation.phone_required");
        if (data.service.trim().length < 2) errs.service = t("validation.service_required");
        return errs;
    };

    const handleAppOpen = (e: React.MouseEvent, target: string) => {
        if (target !== "#download-app") return;
        
        // If it's the download link, scroll to it
        const el = document.getElementById('download-app');
        if (el) {
            e.preventDefault();
            el.scrollIntoView({ behavior: 'smooth' });
        }

        // Try to open the client app by default if on mobile
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        if (isMobile && target === "#download-app") {
            const packageId = "com.almasria.medical_waste_client";
            if (/Android/i.test(navigator.userAgent)) {
                window.location.href = `intent://open#Intent;scheme=almasria;package=${packageId};end`;
            } else {
                window.location.href = "almasria://open";
            }
        }
    };

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        setErrors({});
        const errs = validate(values);
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            return;
        }

        setIsLoading(true);
        
        // Sanitize phone: remove any non-digit characters except the leading plus
        const sanitizedPhone = values.phone.replace(/(?!\+)[^\d]/g, '');

        try {
            const response = await api.submitRequest({
                name: values.name,
                facility_type: values.facilityType,
                email: values.email,
                phone: sanitizedPhone,
                service_type: values.service,
                details: values.details
            }, locale);
            console.log("Request sent successfully:", response);
            setIsSubmitted(true);
        } catch (error: any) {
            console.error("Submission error:", error);
            
            // Extract the user-friendly message from the server if available
            const serverMessage = error.message;
            const hasServerMessage = serverMessage && !serverMessage.startsWith('API_ERROR');

            const errorMsg = hasServerMessage 
                ? serverMessage 
                : t("error_alert");
            
            alert(errorMsg);
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <section id="request-service" className="py-32 relative overflow-hidden bg-white dark:bg-slate-950">
             {/* Decorative background blobs */}
             <div className="absolute top-0 left-0 w-full h-[600px] bg-gradient-to-b from-primary/5 to-transparent -z-10" />
             <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-secondary/10 rounded-full blur-[120px] -z-10 animate-pulse" />
             
             <div className="container relative z-10 px-4 md:px-6">
                 <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-stretch">
                     
                     {/* Information Sidebar */}
                     <div className="flex flex-col justify-center space-y-12">
                        <div 
                            className="space-y-6"
                        >
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-secondary/10 text-secondary text-sm font-bold border border-secondary/20 shadow-sm">
                                <MessageSquare className="w-4 h-4" /> {t("contact_badge")}
                            </div>
                            <h2 className="text-4xl md:text-6xl font-black font-heading text-primary dark:text-white leading-[1.1] tracking-tighter">
                                {t("title")}
                            </h2>
                            <p className="text-xl text-slate-600 dark:text-slate-400 leading-relaxed max-w-xl">
                                {t("subtitle")}
                            </p>
                        </div>

                        <div className="space-y-6">
                            {[
                                { icon: LayoutDashboard, title: t("existing_client_title"), desc: t("existing_client_desc"), link: "#download-app", buttonText: t("portal_button") },
                                { icon: Smartphone, title: t("app_download_title"), desc: t("app_download_desc"), link: "#download-app", buttonText: t("app_download_button") },
                            ].map((item, idx) => (
                                <div 
                                    key={idx}
                                    className="p-8 glass-card rounded-3xl border-primary/5 hover:border-primary/20 group"
                                >
                                    <div className="flex items-start gap-6">
                                        <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center text-white shrink-0 group-hover:scale-110 transition-transform">
                                            <item.icon className="w-7 h-7" />
                                        </div>
                                        <div className="space-y-3">
                                            <h3 className="text-2xl font-bold text-primary dark:text-white">{item.title}</h3>
                                            <p className="text-slate-600 dark:text-slate-400 text-base">{item.desc}</p>
                                            <Button 
                                                variant="link" 
                                                className="px-0 text-secondary font-bold text-lg hover:gap-3 transition-all h-auto" 
                                                asChild
                                                onClick={(e) => handleAppOpen(e, item.link)}
                                            >
                                                <Link href={item.link}>
                                                    {item.buttonText} <ArrowRight className="ms-2 w-5 h-5 rtl:rotate-180" />
                                                </Link>
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Fast Support Badge */}
                        <div className="flex items-center gap-4 p-6 rounded-2xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-white/5 w-fit">
                            <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-500">
                                <Clock className="w-5 h-5" />
                            </div>
                            <p className="text-sm font-bold text-slate-600 dark:text-slate-400">
                                {t("response_text")} <span className="text-primary dark:text-white">{t("response_time")}</span>
                            </p>
                        </div>
                     </div>

                     {/* Form Card */}
                     <div
                        className="relative"
                     >
                         <Card className="border-0 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.1)] dark:shadow-none bg-white dark:bg-slate-900/50 backdrop-blur-xl rounded-[3rem] overflow-hidden sticky top-32">
                             <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-bl-full -mr-10 -mt-10" />
                             
                             <CardHeader className="p-10 pb-4 space-y-2">
                                 <CardTitle className="text-3xl text-primary dark:text-white font-black tracking-tighter">{t("form_title")}</CardTitle>
                                 <CardDescription className="text-lg text-slate-500">{t("form_desc")}</CardDescription>
                             </CardHeader>
                             
                             <CardContent className="p-10 pt-6">
                                {isSubmitted ? (
                                    <div 
                                        className="flex flex-col items-center justify-center py-20 text-center space-y-6"
                                    >
                                        <div className="w-24 h-24 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-4 ring-8 ring-emerald-50 dark:ring-emerald-950/20">
                                            <CheckCircle className="w-12 h-12 text-emerald-600 dark:text-emerald-400" />
                                        </div>
                                        <h3 className="text-3xl font-black text-primary dark:text-white">{t("success_title")}</h3>
                                        <p className="text-xl text-slate-500 max-w-sm mx-auto leading-relaxed">{t("success_desc")}</p>
                                        <Button variant="outline" size="lg" onClick={() => { setIsSubmitted(false); setValues(EMPTY_FORM); }} className="mt-8 rounded-2xl px-10 h-14 font-bold border-slate-200">{t("reset_button")}</Button>
                                    </div>
                                ) : (
                                 <form 
                                    onSubmit={onSubmit} 
                                    className="space-y-8"
                                 >
                                     <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                         <div className="space-y-3">
                                             <Label htmlFor="name" className="text-sm font-bold text-slate-600 dark:text-slate-400 ms-1">{t("labels.name")}</Label>
                                             <Input id="name" value={values.name} onChange={setField("name")} placeholder={t("placeholders.name")} className="h-14 rounded-2xl bg-slate-50 dark:bg-slate-950/50 border-transparent focus:border-primary/20 transition-all text-lg" />
                                             {errors.name && <p className="text-xs text-red-500 font-medium">{errors.name}</p>}
                                         </div>
                                         <div className="space-y-3">
                                             <Label htmlFor="facility" className="text-sm font-bold text-slate-600 dark:text-slate-400 ms-1">{t("labels.facility")}</Label>
                                             <Input id="facility" value={values.facilityType} onChange={setField("facilityType")} placeholder={t("placeholders.facility")} className="h-14 rounded-2xl bg-slate-50 dark:bg-slate-950/50 border-transparent focus:border-primary/20 transition-all text-lg" />
                                             {errors.facilityType && <p className="text-xs text-red-500 font-medium">{errors.facilityType}</p>}
                                         </div>
                                     </div>
                                     <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                         <div className="space-y-3">
                                             <Label htmlFor="email" className="text-sm font-bold text-slate-600 dark:text-slate-400 ms-1">{t("labels.email")}</Label>
                                             <Input id="email" type="email" value={values.email} onChange={setField("email")} placeholder={t("placeholders.email")} className="h-14 rounded-2xl bg-slate-50 dark:bg-slate-950/50 border-transparent focus:border-primary/20 transition-all text-lg" />
                                             {errors.email && <p className="text-xs text-red-500 font-medium">{errors.email}</p>}
                                         </div>
                                         <div className="space-y-3">
                                             <Label htmlFor="phone" className="text-sm font-bold text-slate-600 dark:text-slate-400 ms-1">{t("labels.phone")}</Label>
                                             <Input id="phone" value={values.phone} onChange={setField("phone")} placeholder="+20 12X XXX XXXX" className="h-14 rounded-2xl bg-slate-50 dark:bg-slate-950/50 border-transparent focus:border-primary/20 transition-all text-lg" />
                                             {errors.phone && <p className="text-xs text-red-500 font-medium">{errors.phone}</p>}
                                         </div>
                                     </div>
                                     <div className="space-y-3">
                                         <Label htmlFor="service" className="text-sm font-bold text-slate-600 dark:text-slate-400 ms-1">{t("labels.service")}</Label>
                                         <div className="relative">
                                             <select 
                                                 id="service"
                                                 className="flex h-14 w-full rounded-2xl border-transparent bg-slate-50 dark:bg-slate-950/50 ps-4 pe-10 py-2 text-lg ring-offset-background appearance-none focus-visible:outline-none focus:border-primary/20 transition-all disabled:opacity-50"
                                                 value={values.service}
                                                 onChange={setField("service")}
                                             >
                                                 <option value="">{t("services.select")}</option>
                                                 <option value="pickup">{t("services.pickup")}</option>
                                                 <option value="hazardous">{t("services.hazardous")}</option>
                                                 <option value="sharps">{t("services.sharps")}</option>
                                                 <option value="consultation">{t("services.consultation")}</option>
                                             </select>
                                             <div className="absolute right-4 top-5 pointer-events-none rtl:right-auto rtl:left-4">
                                                 <ArrowRight className="h-5 w-5 rotate-90 text-slate-400" />
                                             </div>
                                         </div>
                                         {errors.service && <p className="text-xs text-red-500 font-medium">{errors.service}</p>}
                                     </div>
                                     <div className="space-y-3">
                                         <Label htmlFor="details" className="text-sm font-bold text-slate-600 dark:text-slate-400 ms-1">{t("labels.details")}</Label>
                                         <Textarea id="details" value={values.details} onChange={setField("details")} placeholder={t("placeholders.details")} className="min-h-[120px] rounded-2xl bg-slate-50 dark:bg-slate-950/50 border-transparent focus:border-primary/20 transition-all text-lg p-4" />
                                     </div>
                                     
                                     <Button 
                                        type="submit" 
                                        size="lg"
                                        className="w-full font-black h-16 text-xl rounded-2xl shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3 group"
                                        disabled={isLoading}
                                     >
                                         {isLoading ? (
                                             <><Loader2 className="w-6 h-6 animate-spin" /> {t("loading")}</>
                                         ) : (
                                             <>{t("submit")} <Send className="w-6 h-6 rtl:rotate-180 transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" /></>
                                         )}
                                     </Button>
                                 </form>
                                )}
                             </CardContent>
                         </Card>
                     </div>
                 </div>
             </div>
        </section>
    )
}